import { Link } from "react-router-dom";
import {
  GitFork, Network, Scale, Sparkles, ShieldQuestion, Activity,
  ArrowRight, Eye, MessageSquare,
} from "lucide-react";

export default function About() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">How Idea Strains works</h1>
        <p className="text-[13px] text-[var(--color-ink-muted)] mt-1">
          Plain, casual, and clear. No philosophy, debate-theory, or Latin required.
        </p>
      </div>

      <Block title="What is a strain?" icon={<Network size={16} />}>
        <p>A <strong>strain</strong> is a published idea — a claim, argument, proposal, hypothesis, hot take — that's been turned into a forkable map of connected claims and evidence. Instead of one big wall of text or a pile of comments, a strain is a living structure you can inspect and change.</p>
      </Block>

      <Block title="What is a fork?" icon={<GitFork size={16} />}>
        <p>A <strong>fork</strong> is a new version of a strain that diverges from the original. It's not a copy — it records exactly what changed: which claims were added, removed, reworded, or challenged. You can fork the whole strain, one branch, or a single node.</p>
      </Block>

      <Block title="What is a crux?" icon={<Scale size={16} />}>
        <p>A <strong>crux</strong> is a load-bearing point where the whole idea could change if this point were different. It might be a hidden assumption, a disputed definition, a causal inference, or a value criterion. The app surfaces cruxes so you can find where the real disagreement lives — instead of arguing past each other.</p>
      </Block>

      <Block title="Why not just comments?" icon={<MessageSquare size={16} />}>
        <p>Normal online debate creates disposable comments that disappear into threads. Idea Strains creates durable artifacts: structures you can revisit, fork, and improve. Comments are anchored to specific nodes and edges, so they stay connected to the idea — not floating in a void.</p>
      </Block>

      <Block title="Why no normal likes?" icon={<Activity size={16} />}>
        <p>Likes reward cheap agreement, dunking, and tribal pile-ons. Instead, we surface signals that actually tell you something: <em>did this change minds? did people from different camps find this fair? did it locate the real disagreement?</em> In early versions that's before/after stance sliders and reaction chips.</p>
      </Block>

      <Block title="How AI suggestions work" icon={<Sparkles size={16} />}>
        <p>When you paste prose, the AI drafts a map: claims, evidence, hidden warrants, likely cruxes. Every AI suggestion is labeled <strong className="text-[var(--color-inferred)]">inferred</strong> until you accept it. The AI never silently rewrites your claims, never declares a winner, and always preserves your original wording.</p>
      </Block>

      <Block title="How to challenge an idea constructively" icon={<ShieldQuestion size={16} />}>
        <p>Select a node or edge and hit <strong>Challenge</strong>. You can challenge a claim, an inference, a source, a definition, or a scope condition. The durable object is the map, not the fight — your challenge stays anchored to the specific point and can be forked into a new version.</p>
      </Block>

      <Block title="The special edge: undercuts" icon={<Eye size={16} />}>
        <p>Most debate tools only let you attack claims. Idea Strains has <strong>undercuts</strong>: edges that attack the <em>inference between two claims</em>. "Even if that claim is true, it doesn't support the conclusion you're using it for." That's where a huge amount of real disagreement actually lives.</p>
      </Block>

      <Block title="How to read the graph" icon={<ArrowRight size={16} />}>
        <div className="space-y-2">
          <p>The canvas is a directed graph. Different line colors and styles mean different relationships:</p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Legend color="var(--color-support)" dash="solid" label="supports" />
            <Legend color="var(--color-rebut)" dash="solid" label="rebuts" />
            <Legend color="var(--color-undercut)" dash="dashed" label="undercuts" />
            <Legend color="var(--color-evidence)" dash="dotted" label="evidences" />
          </div>
          <p className="mt-2">Violet nodes are AI-inferred and not yet accepted. Amber-pulsing nodes are cruxes. The thesis sits on the left.</p>
        </div>
      </Block>

      <div className="mt-8 rounded-2xl border border-[var(--color-primary)] bg-[rgba(94,234,212,0.05)] p-5 text-center">
        <p className="text-[14px] text-[var(--color-ink)] font-medium mb-3">Ready to see it in action?</p>
        <Link to="/s/college-worth-it" className="inline-flex items-center gap-1.5 text-[var(--color-primary)] font-semibold hover:underline">
          Explore the college-worth-it strain <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  );
}

function Block({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[var(--color-primary)]">{icon}</span>
        <h2 className="text-base font-semibold">{title}</h2>
      </div>
      <div className="prose-idea text-[var(--color-ink-soft)] pl-6">{children}</div>
    </div>
  );
}

function Legend({ color, dash, label }: { color: string; dash: string; label: string }) {
  return (
    <div className="flex items-center gap-2 text-[12px] text-[var(--color-ink-muted)]">
      <svg width="28" height="6"><line x1="0" y1="3" x2="28" y2="3" stroke={color} strokeWidth="2" strokeDasharray={dash === "dashed" ? "4 3" : dash === "dotted" ? "1.5 3" : undefined} /></svg>
      {label}
    </div>
  );
}
